import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Copy, Check, Clipboard } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/context/AuthContext';
import { getHotelData } from '@/lib/services/hotelService';
import { HotelData } from '@/lib/types/auth';

const EmbedCode = () => {
  const [copied, setCopied] = useState(false);
  const [hotelInfo, setHotelInfo] = useState<HotelData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { auth } = useAuth();
  
  useEffect(() => {
    const loadHotelData = async () => {
      if (auth.user) {
        try {
          const data = await getHotelData();
          if (data) {
            setHotelInfo(data);
          }
        } catch (error) {
          console.error('Error loading hotel data:', error);
          toast.error('Failed to load hotel data');
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadHotelData();
  }, [auth.user]);
  
  const embedCode = `<div 
  id="LobierChatWidget"
  data-id="${hotelInfo?.id || ''}"
  data-position="bottom-right"
  data-primary-color="#1A365D"
  data-lang="EN"
  data-secondary-color="#4299E1"></div>
  <script src="https://lobier.com/embed/lobier-chat.js" defer></script>`;
  
  const handleCopyCode = () => {
    navigator.clipboard.writeText(embedCode)
      .then(() => {
        setCopied(true);
        toast.success('Code copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        toast.error('Failed to copy code');
      });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hotel-primary"></div>
            <p className="mt-2 text-gray-600">Loading your hotel information...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Embed Your Chat Widget</CardTitle>
            <CardDescription>
              Add this code to your website to enable the AI chat assistant
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="embedCode">Embed Code</Label>
              <div className="relative">
                <div 
                  className="p-4 rounded-md bg-gray-900 text-gray-100 font-mono text-sm overflow-x-auto"
                >
                  {embedCode}
                </div>
                <Button 
                  variant="outline" 
                  size="icon"
                  className="absolute top-3 right-3 bg-white/10 border-white/20 hover:bg-white/20"
                  onClick={handleCopyCode}
                >
                  {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4 text-white" />}
                </Button>
              </div>
            </div>
            
            <div className="space-y-4 bg-gray-50 p-5 rounded-md border">
              <h3 className="font-medium text-lg flex items-center">
                <Clipboard className="mr-2 h-5 w-5 text-hotel-secondary" />
                Installation Instructions
              </h3>
              
              <div className="space-y-4 text-gray-600">
                <div>
                  <h4 className="font-medium text-gray-800">1. Copy the code above</h4>
                  <p>Click the copy button to copy the embed code to your clipboard.</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-800">2. Add to your website</h4>
                  <p>Paste the code into the HTML of your website, just before the closing <code className="bg-gray-200 px-1 rounded">&lt;/body&gt;</code> tag.</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-800">3. That's it!</h4>
                  <p>The chat widget will automatically appear on your website. Your hotel information and FAQs will be used to power the AI responses.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default EmbedCode;
